import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TeeSectionHeader from '@/components/ui/tee-section-header';
import { TeeLoader } from '@/components/ui/tee-loader';
import { TeeButton } from '@/components/ui/tee-button';
import { TeePlaylistReorderList } from '@/components/ui/tee-playlist-reorder-list';
import PlaylistFormModal from '@/pages/content-library/PlaylistFormModal';
import ContentItemPreviewModal from './ContentItemPreviewModal';
import ContentPickerPanel from './ContentPickerPanel';
import { playlistService } from '@/services/playlist.service';
import { contentItemService } from '@/services/content-item.service';
import { Playlist } from '@/types/playlist';
import { ContentItem, ContentStatus } from '@/types/contentItem';
import { showErrorToast } from '@/utils/toast.utils';
import { MEDIA_TYPE_LABEL_KEY } from '@/utils/contentMediaType';

export default function PlaylistBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [playlist, setPlaylist] = useState<Playlist | null>(null);
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<ContentItem | null>(null);

  // Shared by the mount effect and PlaylistFormModal's onSaved handler so a
  // rename refreshes the header title and every other surface from the
  // server, rather than patching local state.
  const loadPlaylist = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(false);
    try {
      const loaded = await playlistService.getPlaylist(id);
      setPlaylist(loaded);

      // PlaylistResponseDto returns contentItemIds only — hydrate each id
      // into a full ContentItem, then re-map over contentItemIds (not the
      // Promise.all resolution order) so a dropped/missing id can never
      // desync the rendered order from the stored order.
      const resolved = await Promise.all(
        loaded.contentItemIds.map((itemId) =>
          contentItemService.getContentItem(itemId).catch(() => null)
        )
      );
      const itemById = new Map<string, ContentItem>();
      resolved.forEach((item) => {
        if (item) itemById.set(item.id, item);
      });
      const ordered = loaded.contentItemIds
        .map((itemId) => itemById.get(itemId))
        .filter((item): item is ContentItem => !!item);
      setItems(ordered);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadPlaylist();
  }, [loadPlaylist]);

  const handleReorder = async (orderedIds: string[]) => {
    if (!id) return;
    // ReorderPlaylistDto carries @ArrayNotEmpty() — removal of the last item
    // goes through handleRemove -> PUT :id instead.
    if (orderedIds.length === 0) return;

    const previousItems = items;
    const itemById = new Map(items.map((item) => [item.id, item]));
    const nextItems = orderedIds
      .map((itemId) => itemById.get(itemId))
      .filter((item): item is ContentItem => !!item);

    setItems(nextItems);
    setSaving(true);
    try {
      await playlistService.reorderPlaylist(id, orderedIds);
    } catch {
      setItems(previousItems);
      showErrorToast(t('contentLibrary.errorReorder'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (itemId: string) => {
    if (!id || !playlist) return;
    const nextItems = items.filter((item) => item.id !== itemId);
    const nextIds = nextItems.map((item) => item.id);

    setSaving(true);
    try {
      const updated = await playlistService.updatePlaylist(id, {
        name: playlist.name,
        contentItemIds: nextIds,
      });
      setItems(nextItems);
      setPlaylist(updated);
    } catch {
      showErrorToast(t('contentLibrary.errorSavePlaylist'));
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async (item: ContentItem) => {
    if (!id || !playlist) return;
    if (items.some((existing) => existing.id === item.id)) return;
    const nextIds = [...items.map((i) => i.id), item.id];

    setSaving(true);
    try {
      const updated = await playlistService.updatePlaylist(id, {
        name: playlist.name,
        contentItemIds: nextIds,
      });
      setItems([...items, item]);
      setPlaylist(updated);
    } catch {
      showErrorToast(t('contentLibrary.errorSavePlaylist'));
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!id || !playlist) return;
    try {
      if (playlist.status === ContentStatus.PUBLISHED) {
        await playlistService.unpublishPlaylist(id);
      } else {
        await playlistService.publishPlaylist(id);
      }
      await loadPlaylist();
    } catch {
      showErrorToast(t('contentLibrary.errorStatusUpdate'));
    }
  };

  if (loading) {
    return (
      <div className="section_container">
        <TeeLoader />
      </div>
    );
  }

  if (error || !playlist) {
    return (
      <div className="section_container">
        <div className="flex flex-col items-center gap-4 py-12">
          <p className="text-body-m text-[var(--text-content-sub-title)]">
            {t('contentLibrary.errorLoadPlaylists')}
          </p>
          <TeeButton
            variant="secondary"
            text={t('contentLibrary.retry')}
            onClick={() => void loadPlaylist()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="section_container">
      <TeeSectionHeader
        title={playlist.name}
        description={
          <div className="flex flex-col gap-1">
            <span>
              {t('contentLibrary.itemsCount', { count: items.length })} ·{' '}
              {t(
                playlist.status === ContentStatus.PUBLISHED
                  ? 'contentLibrary.statusPublished'
                  : 'contentLibrary.statusDraft'
              )}
            </span>
            {playlist.description && (
              <span className="text-body-s text-[var(--text-content-sub-title)]">
                {playlist.description}
              </span>
            )}
          </div>
        }
        onTitleEdit={() => setRenameOpen(true)}
        secondaryActionLabel={t(
          playlist.status === ContentStatus.PUBLISHED
            ? 'contentLibrary.unpublishPlaylist'
            : 'contentLibrary.publishPlaylist'
        )}
        onSecondaryAction={handleTogglePublish}
        doneActionLabel={t('common.done')}
        onDoneAction={() => navigate('/content-library/studio?tab=playlists')}
      />

      <div className="flex w-full gap-8 pt-6">
        <div className="flex-1 flex flex-col gap-3">
          <p className="text-heading-h4 font-semibold text-[var(--text-content-title)]">
            {t('contentLibrary.builderListTitle')}
          </p>
          <TeePlaylistReorderList
            items={items.map((item) => ({
              id: item.id,
              title: item.title,
              mediaType: item.mediaType,
              thumbnailUrl:
                item.videoThumbnailUrl ?? item.imageSignedUrl ?? item.imageUrl ?? null,
            }))}
            onReorder={handleReorder}
            onRemove={handleRemove}
            onPreview={(itemId) => {
              const target = items.find((item) => item.id === itemId);
              if (target) setPreviewTarget(target);
            }}
            disabled={saving}
            emptyText={t('contentLibrary.emptyBuilder')}
            dragHandleLabel={(title) => t('contentLibrary.ariaDragToReorder', { title })}
            removeLabel={(title) => t('contentLibrary.ariaRemoveFromPlaylist', { title })}
            previewLabel={(title) => t('contentLibrary.ariaPreview', { title })}
            mediaTypeLabel={(mediaType) => t(MEDIA_TYPE_LABEL_KEY[mediaType])}
          />
        </div>

        <div className="w-[400px] shrink-0 flex flex-col gap-3">
          <p className="text-heading-h4 font-semibold text-[var(--text-content-title)]">
            {t('contentLibrary.builderSearchTitle')}
          </p>
          <ContentPickerPanel
            excludeIds={items.map((item) => item.id)}
            onAdd={handleAdd}
            disabled={saving}
          />
        </div>
      </div>

      <PlaylistFormModal
        isOpen={renameOpen && playlist !== null}
        editTarget={playlist}
        onClose={() => setRenameOpen(false)}
        onSaved={() => {
          setRenameOpen(false);
          void loadPlaylist();
        }}
      />

      <ContentItemPreviewModal
        isOpen={previewTarget !== null}
        item={previewTarget}
        onClose={() => setPreviewTarget(null)}
      />
    </div>
  );
}
